import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Loader from "../components/Loader";
import { crmHeaders } from "../utils/crmApi";
import { canManageFollowUps, hasFullAccess } from "../utils/userAccess";
import Cookies from "js-cookie";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const EMPTY_FORM = {
    contactName: "",
    contactEmail: "",
    businessName: "",
    subject: "",
    note: "",
};

export default function FollowUps() {
    const username = Cookies.get("username");
    const isAdmin = hasFullAccess();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ ...EMPTY_FORM });
    const [editId, setEditId] = useState(null);
    const [search, setSearch] = useState("");

    const loadItems = () => {
        setLoading(true);
        axios
            .get(`${BASE_URL}/user-follow-ups`, { headers: crmHeaders() })
            .then((res) => setItems(res.data || []))
            .catch((err) => {
                toast.error(err.response?.data?.message || "Failed to load follow-ups");
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (!canManageFollowUps()) return;
        loadItems();
    }, []);

    if (!canManageFollowUps()) {
        return (
            <div className="container p-4">
                <h3>Access Denied</h3>
                <p className="text-muted">Follow-up sheet access is not enabled for your account.</p>
            </div>
        );
    }

    const resetForm = () => {
        setForm({ ...EMPTY_FORM });
        setEditId(null);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.contactName.trim()) {
            toast.error("Name is required");
            return;
        }
        setSaving(true);
        const payload = {
            contactName: form.contactName,
            contactEmail: form.contactEmail,
            businessName: form.businessName,
            subject: form.subject,
            note: form.note,
        };
        const req = editId
            ? axios.put(`${BASE_URL}/user-follow-ups/${editId}`, payload, { headers: crmHeaders() })
            : axios.post(`${BASE_URL}/user-follow-ups`, payload, { headers: crmHeaders() });

        req
            .then(() => {
                toast.success(editId ? "Follow-up updated" : "Follow-up added");
                resetForm();
                loadItems();
            })
            .catch((err) => {
                toast.error(err.response?.data?.message || "Save failed");
            })
            .finally(() => setSaving(false));
    };

    const handleEdit = (row) => {
        setEditId(row.Id);
        setForm({
            contactName: row.Contact_Name || "",
            contactEmail: row.Contact_Email || "",
            businessName: row.Business_Name || "",
            subject: row.Subject || "",
            note: row.Note || "",
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleDelete = (row) => {
        if (!window.confirm("Delete this follow-up?")) return;
        axios
            .delete(`${BASE_URL}/user-follow-ups/${row.Id}`, { headers: crmHeaders() })
            .then(() => {
                toast.success("Deleted");
                if (editId === row.Id) resetForm();
                loadItems();
            })
            .catch((err) => toast.error(err.response?.data?.message || "Delete failed"));
    };

    const filtered = items.filter((row) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (
            (row.Contact_Name || "").toLowerCase().includes(q) ||
            (row.Contact_Email || "").toLowerCase().includes(q) ||
            (row.Business_Name || "").toLowerCase().includes(q) ||
            (row.Subject || "").toLowerCase().includes(q) ||
            (row.Note || "").toLowerCase().includes(q) ||
            (row.User_Name || "").toLowerCase().includes(q)
        );
    });

    if (loading) return <Loader />;

    return (
        <div className="container crm-follow-ups-page">
            <ToastContainer />
            <div className="crm-page-header">
                <div>
                    <h1 className="crm-page-title">Follow-Up Sheet</h1>
                    <p className="crm-page-subtitle">
                        {isAdmin
                            ? "View and manage all team follow-ups"
                            : `Your personal follow-ups — ${username}`}
                    </p>
                </div>
            </div>

            <div className="crm-user-form-card mb-4">
                <div className="crm-user-form-card-header">
                    <i className="bi bi-journal-plus"></i>
                    <div>
                        <h5>{editId ? "Edit Follow-Up" : "Add Follow-Up"}</h5>
                        <span>Name, email, business, subject and notes</span>
                    </div>
                </div>
                <div className="crm-user-form-card-body">
                    <form onSubmit={handleSubmit}>
                        <div className="row g-3">
                            <div className="col-md-6">
                                <label className="form-label">Name *</label>
                                <input
                                    className="form-control"
                                    value={form.contactName}
                                    onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Email</label>
                                <input
                                    type="email"
                                    className="form-control"
                                    value={form.contactEmail}
                                    onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Business Name</label>
                                <input
                                    className="form-control"
                                    value={form.businessName}
                                    onChange={(e) => setForm({ ...form, businessName: e.target.value })}
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label">Subject</label>
                                <input
                                    className="form-control"
                                    value={form.subject}
                                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                                />
                            </div>
                            <div className="col-12">
                                <label className="form-label">Note</label>
                                <textarea
                                    className="form-control"
                                    rows={4}
                                    value={form.note}
                                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="mt-3 d-flex gap-2">
                            <button type="submit" className="btn btn-primary" disabled={saving}>
                                {saving ? "Saving..." : editId ? "Update" : "Add Follow-Up"}
                            </button>
                            {editId && (
                                <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                                    Cancel Edit
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </div>

            <div className="crm-follow-table-wrap">
                <div className="p-3 border-bottom d-flex flex-wrap gap-2 align-items-center justify-content-between">
                    <strong>{filtered.length} record{filtered.length !== 1 ? "s" : ""}</strong>
                    <input
                        type="search"
                        className="form-control"
                        style={{ maxWidth: 280 }}
                        placeholder="Search follow-ups..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div className="table-responsive">
                    <table className="table table-hover mb-0">
                        <thead>
                            <tr>
                                {isAdmin && <th>User</th>}
                                <th>Name</th>
                                <th>Email</th>
                                <th>Business</th>
                                <th>Subject</th>
                                <th>Note</th>
                                <th>Date</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={isAdmin ? 8 : 7} className="text-center text-muted py-4">
                                        No follow-ups yet
                                    </td>
                                </tr>
                            ) : (
                                filtered.map((row) => (
                                    <tr key={row.Id}>
                                        {isAdmin && <td>{row.User_Name}</td>}
                                        <td>{row.Contact_Name}</td>
                                        <td>{row.Contact_Email || "—"}</td>
                                        <td>{row.Business_Name || "—"}</td>
                                        <td>{row.Subject || "—"}</td>
                                        <td style={{ maxWidth: 220 }}>{row.Note || "—"}</td>
                                        <td>{new Date(row.Created_At).toLocaleString()}</td>
                                        <td className="text-nowrap">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary me-1"
                                                onClick={() => handleEdit(row)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => handleDelete(row)}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
